'use client';

import Link from 'next/link';
import type { Route } from 'next';
import { usePathname } from 'next/navigation';
import { theme } from './theme';
import type { Locale } from './theme';

const LOCALE_COOKIE = 'locusly-locale';
// One year -keep the user's choice sticky.
const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

// Segmented EN/PT toggle. Each option links to the equivalent route, so
// the switch is a real navigation (good for SEO + crawling) rather than
// client-only state.
export function LangSwitcher({ current, compact = false }: { current: Locale; compact?: boolean }) {
  const pathname = usePathname() ?? '/';
  const opts: Locale[] = ['en', 'pt'];

  function urlFor(locale: Locale): string {
    // Replace the leading /pt or /en, or fall back to /<locale>.
    if (pathname === '/pt' || pathname.startsWith('/pt/')) {
      return pathname.replace(/^\/pt/, `/${locale}`);
    }
    if (pathname === '/en' || pathname.startsWith('/en/')) {
      return pathname.replace(/^\/en/, `/${locale}`);
    }
    return `/${locale}`;
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        borderRadius: 999,
        background: theme.c.chip,
        border: `1px solid ${theme.c.border}`,
        fontFamily: theme.fonts.mono,
        fontSize: compact ? 10 : 11,
        letterSpacing: 1,
      }}
    >
      {opts.map((o) => {
        const active = current === o;
        return (
          <Link
            key={o}
            href={urlFor(o) as Route}
            hrefLang={o}
            aria-label={o === 'pt' ? 'Português' : 'English'}
            aria-current={active ? 'page' : undefined}
            onClick={() => {
              // Persist the user's explicit choice so future visits -and the
              // sign-in / sign-up pages on the same browser -render in their
              // preferred language regardless of geo IP.
              if (typeof document !== 'undefined') {
                document.cookie = `${LOCALE_COOKIE}=${o}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax`;
              }
            }}
            style={{
              padding: compact ? '4px 9px' : '6px 12px',
              borderRadius: 999,
              background: active ? theme.c.fg : 'transparent',
              color: active ? theme.c.bg : theme.c.fgMuted,
              fontFamily: 'inherit',
              fontSize: 'inherit',
              letterSpacing: 'inherit',
              textTransform: 'uppercase',
              textDecoration: 'none',
              transition: 'background .15s, color .15s',
            }}
          >
            {o}
          </Link>
        );
      })}
    </div>
  );
}
